// Styled-components wrappers for Inside and Outside layouts
import styled, { createGlobalStyle } from 'styled-components';
import { Layout } from 'antd';

export const LayoutContainer = styled.div`
    /* 100vw includes scrollbar width and can cause horizontal overflow; use 100% of #root */
    width: 100%;
    max-width: 100%;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /*
     * Block layout: the Sider is position:fixed and must not participate as a flex item.
     * With display:flex, ant-layout-sider can still reserve width in the flex line, which
     * stacks with MainContentArea’s margin-left + calc(width) and overflows horizontally
     * (often visible only in production/minified builds).
     */
    display: block;
`;

export const MainContentArea = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    width: ${({ $sidebarWidth, $isMobile }) => ($isMobile ? '100%' : `calc(100% - ${$sidebarWidth}px)`)};
    height: 100vh;
    min-height: 100vh;
    overflow: hidden;
    overflow-x: hidden;
    margin-left: ${({ $sidebarWidth, $isMobile }) => ($isMobile ? 0 : `${$sidebarWidth}px`)};
    transition: margin-left 0.2s ease, width 0.2s ease;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#141414' : '#f0f0f0';
    }};
    
    
    /* Scrollbar styling lives on the body scroll container */
`;

export const BodyContainer = styled.div.attrs({ 'data-main-content-scroll': true })`
    flex: 1;
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    box-sizing: border-box;
    min-height: 0;
    min-width: 0;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colors.border.default};
        border-radius: 4px;
        
        &:hover {
            background: ${({ theme }) => theme.colors.text.secondary};
        }
    }
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.border.default} transparent;
`;

export const ContentWrapper = styled.div`
    width: 100%;
    max-width: 100%;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    flex: 1;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#f0f0f0';
    }};
    box-sizing: border-box;
    overflow-y: visible;
    min-width: 0;
    /* Let BodyContainer own vertical scroll; min-height: 100% + tall children caused nested scrollbars */
    min-height: 0;

    & > * {
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
    }

    /* Tablet: slightly tighter gutters; only adjust horizontal — do not zero vertical padding */
    @media (max-width: 1024px) and (min-width: 769px) {
        padding-left: 16px;
        padding-right: 16px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: ${({ theme }) => theme.spacing.md};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        padding: ${({ theme }) => theme.spacing.sm};
    }
`;

export const HeaderWrapper = styled(Layout.Header)`
  background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#f0f0f0';
    }} !important;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: auto;
    min-height: auto;
    /* Sticky so header stays visible while MainContentArea scrolls */
    position: sticky;
    top: 0;
    z-index: 100;
    gap: ${({ theme }) => theme.spacing.md};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: 10px ${({ theme }) => theme.spacing.md};
        height: auto;
        min-height: auto;
    }
`;

export const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex: 1;
    min-width: 0;
    justify-content: flex-start;

    @media (max-width: 1024px) {
        justify-content: center;
    }
`;

export const HeaderTitle = styled.h1`
    font-family: var(--font-family-lufga);
    font-weight: var(--font-weight-lufga-bold);
    font-size: 1.5rem;
    margin: 0;
    text-align: left;

    @media (max-width: 1024px) {
        text-align: center;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        font-size: 1.25rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        font-size: 1.125rem;
    }
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;
    margin-right: 32px;
    justify-content: ${({ $isMobile }) => $isMobile ? 'flex-end' : 'flex-start'};

    @media (max-width: 1024px) {
        justify-content: flex-end;
    }
`;

export const ThemeToggleButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 12px 0 0 0;
    display: flex;
    align-items: start;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: color 0.2s;
    font-size: 1.5rem;
    margin-bottom: 15px;

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
    }
`;

export const SidebarThemeToggleButton = styled.button`
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.05)' : '#F0F0F0';
    }};
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : '#DCE1E5';
    }};
    border-radius: 8px;
    cursor: pointer;
    padding: ${({ $collapsed }) => $collapsed ? '12px' : '12px 20px'};
    display: flex;
    align-items: center;
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    gap: ${({ theme, $collapsed }) => $collapsed ? 0 : theme.spacing.md};
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: all 0.3s ease;
    width: 100%;
    margin-top: ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;
    font-size: 1.25rem;

    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.08)' : '#F5F5F5';
    }};
        color: ${({ theme }) => theme.colors.text.primary};
    }

    .anticon {
        font-size: 1.25rem;
        flex-shrink: 0;
    }
`;

export const SearchInputWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;
    display: flex;
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        max-width: 300px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        max-width: 200px;
    }
`;

export const KeyboardShortcutContainer = styled.div`
    display: flex;
    gap: 2px;
    align-items: center;
`;

export const KeyboardShortcut = styled.span`
    background: ${({ theme }) => theme.colors.background.tertiary};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 0.75rem;
    font-family: monospace;
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1;
`;

export const SiderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-height: 100vh;
    width: 100%;
    min-width: 100%;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }};
    border-right: 1px solid ${({ theme }) => theme.colors.border.default};
    transition: background 0.2s ease;
    padding: 0 10px 0 10px;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    resize: none;
    box-sizing: border-box;
    
    /* Reset direction for all children to LTR */
    & > * {
        direction: ltr;
    }
    
    /* Hide scrollbar when not needed */
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 3px;
        transition: background 0.2s ease;
    }
    
    &:hover::-webkit-scrollbar-thumb {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }};
    }
    
    /* Firefox scrollbar - hide when not needed */
    scrollbar-width: none;
    
    &:hover {
        scrollbar-width: thin;
        scrollbar-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }} transparent;
    }

    @media (max-width: 1280px) {
        display: none !important;
    }
`;

export const StickyTopSection = styled.div`
    position: relative;
    z-index: 1;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    padding-bottom: ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;
`;

export const LogoContainer = styled.div`
    padding: ${({ theme }) => theme.spacing.md};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.sm};
    min-height: 50px;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: ${({ theme }) => theme.spacing.md};
        min-height: 56px;
    }
`;

export const LogoImage = styled.img`
    height: 50px;
    width: 125px;
    object-fit: contain;
    margin-top: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        height: 50px;
        width: 125px;
    }
`;

export const UserProfileSection = styled.div`
    padding: 0 20px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    gap: ${({ theme, $collapsed }) => $collapsed ? 0 : theme.spacing.md};
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.05)' : '#F0F0F0';
    }};
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : '#DCE1E5';
    }};
    border-radius: 8px;
    margin-top: ${({ $compact }) => ($compact ? '8px' : '30px')};
    cursor: pointer;
    transition: all 0.3s ease;
    box-sizing: border-box;
    
    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.08)' : '#F5F5F5';
    }};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: 0 20px;
        height: 64px;
    }
`;

export const UserInfoWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    max-width: ${({ $collapsed }) => $collapsed ? 0 : '100%'};
    transition: opacity 0.3s ease, max-width 0.3s ease, gap 0.3s ease;
`;

export const DropdownIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    font-size: 12px;
    flex-shrink: 0;
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.3s ease;
    
    .anticon {
        font-size: 12px;
    }
`;

export const UserAvatar = styled.div`
    width: ${({ $collapsed }) => $collapsed ? '40px' : '40px'};
    height: ${({ $collapsed }) => $collapsed ? '40px' : '40px'};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: ${({ $collapsed }) => $collapsed ? '0.875rem' : '1rem'};
    flex-shrink: 0;
    transition: all 0.3s ease;
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

export const UserName = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
`;

export const UserRole = styled.div`
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const SidebarDivider = styled.div`
    width: 100%;
    height: 1px;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    }};
    margin: 14px 0 0 0;
    flex-shrink: 0;
`;

export const UserDropdownStyles = createGlobalStyle`
    .user-profile-dropdown.ant-dropdown {
        z-index: 2100;
    }

    .ant-dropdown-menu {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }} !important;
        border-radius: 8px !important;
        box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
    }} !important;
        padding: 8px 0 !important;
        min-width: 200px !important;
        border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5';
    }} !important;
    }

    .ant-dropdown-menu .ant-dropdown-menu-item {
        color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.85)' : '#000000';
    }} !important;
        padding: 10px 16px !important;
        font-family: var(--font-family-lufga) !important;
        border-radius: 8px !important;
        margin: 2px 8px !important;
        transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.18s ease !important;
        
        .anticon {
            color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)';
    }} !important;
        }
        
        &:hover {
            background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.08)' : '#E9EEF5';
    }} !important;
            color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }} !important;
            box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark
            ? 'inset 0 0 0 1px rgba(255,255,255,0.10), 0 4px 12px rgba(0,0,0,0.28)'
            : 'inset 0 0 0 1px rgba(15,23,42,0.12), 0 6px 14px rgba(15,23,42,0.14)';
    }} !important;
            transform: translateY(-1px);
            
            .anticon {
                color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }} !important;
            }
        }

        &:active {
            background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.12)' : '#EBEBEB';
    }} !important;
        }
    }

    .ant-dropdown-menu .ant-dropdown-menu-item-danger {
        color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#FF4D4F' : '#FF4D4F';
    }} !important;
        
        &:hover {
            background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 77, 79, 0.16)' : '#FFF1F0';
    }} !important;
            color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#FF7875' : '#FF4D4F';
    }} !important;
            
            .anticon {
                color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#FF7875' : '#FF4D4F';
    }} !important;
            }
        }
    }
    
    .ant-dropdown-menu-item-selected {
        background: transparent !important;
    }

    /* Force hover/active visuals for profile dropdown in both themes */
    .user-profile-dropdown .ant-dropdown-menu .ant-dropdown-menu-item:hover,
    .user-profile-dropdown .ant-dropdown-menu .ant-dropdown-menu-item-active {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.10)' : '#E9EEF5';
    }} !important;
        box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark
            ? 'inset 0 0 0 1px rgba(255,255,255,0.12), 0 4px 12px rgba(0,0,0,0.30)'
            : 'inset 0 0 0 1px rgba(15,23,42,0.12), 0 6px 14px rgba(15,23,42,0.14)';
    }} !important;
        transform: translateY(-1px);
    }

    .user-profile-dropdown .ant-dropdown-menu .ant-dropdown-menu-item:active {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.12)' : '#EBEBEB';
    }} !important;
    }
`;

export const MenuContainer = styled.div`
    margin-top: 0px;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: ${({ theme }) => theme.spacing.md};
    min-height: 0;
    
    /* Hide scrollbar when not needed */
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 3px;
        transition: background 0.2s ease;
    }
    
    &:hover::-webkit-scrollbar-thumb {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }};
    }
    
    scrollbar-width: none;
    
    &:hover {
        scrollbar-width: thin;
        scrollbar-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }} transparent;
    }

    .ant-menu {
        background: transparent;
        border-right: none !important;
        margin-top: 2px;
        .ant-menu-item {
            border-radius: 6px;
            padding: 9px 0 9px 14px !important;
            height: auto;
            line-height: 1.5;
            border-right: none !important;
            font-size: 16px;
            font-weight: 400;
            color: #8A98A7;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 6px; 
            cursor: pointer !important;
            user-select: none;
            -webkit-user-select: none;
            
            &::after {
                display: none !important;
            }

            .ant-menu-title-content,
            .ant-menu-item-icon,
            span {
                cursor: pointer !important;
                user-select: none;
                -webkit-user-select: none;
            }
            
            .anticon {
                font-size: 20px;
                color: #8A98A7;
                margin-right: 0;
            }
            
            &:hover {
                background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'transparent' : '#E9EEF5';
    }} !important;
                color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#4B5563';
    }};
                box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'none' : 'inset 0 0 0 1px rgba(15,23,42,0.10), 0 4px 10px rgba(15,23,42,0.12)';
    }};
                transform: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'none' : 'translateY(-1px)';
    }};
                
                .anticon {
                    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#4B5563';
    }};
                }
            }

            &:active {
                background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'transparent' : '#DDE5F0';
    }} !important;
                transform: none;
            }
            
            &.ant-menu-item-selected {
                background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'transparent' : '#FFF0F1';
    }} !important;
                color: #F04A4A !important;
                font-weight: 600;
                padding-left: 12px !important;
                padding-right: 0 !important;
                border-left: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '3px solid #F04A4A' : 'none';
    }} !important;
                border-top: none !important;
                border-right: none !important;
                border-bottom: none !important;
                border-radius: 0;
                
                &::after {
                    display: none !important;
                }
                
                .anticon {
                    color: #F04A4A !important;
                    font-size: 24px;
                }
            }
        }
    }
`;

export const CollapseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing.sm};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: all 0.2s;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    
    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => theme.colors.background.tertiary};
    }
    
    .anticon {
        font-size: 18px;
    }
`;

export const HeaderCollapseButton = styled.button`
    background: transparent;
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    
    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: transparent;
    }
    
    .anticon {
        font-size: 16px;
    }
`;

export const SidebarCollapseButton = styled.button`
    position: fixed;
    /* Align with header top padding and keep above it */
    top: 24px;
    left: ${({ $collapsed }) => ($collapsed ? '64px' : '264px')};
    width: 32px;
    height: 32px;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }};
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5';
    }};
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    transition: left 0.2s ease, background 0.2s ease, border-color 0.2s ease;
    z-index: 2001;
    box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)';
    }};
    
    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#3A3A3A' : '#F5F5F5';
    }};
       
    }
    
    svg {
        width: 16px;
        height: 16px;
        stroke-width: 2;
    }

    @media (max-width: 1280px) {
        display: none;
    }
`;

export const MobileDrawerButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    font-size: 20px;
    
    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }};
    }
    
    &:active {
        transform: scale(0.95);
    }

    @media (min-width: 1281px) {
        display: none;
    }
`;

export const DrawerOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
    visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease, visibility 0.3s ease;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);

    @media (min-width: 1281px) {
        display: none;
    }
`;

export const MobileDrawer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(${({ $isOpen }) => $isOpen ? '0' : '-100%'});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    width: 100%;

    @media (min-width: 769px) and (max-width: 1280px) {
        width: 32.5% !important;
    }

    @media (max-width: 768px) {
        width: 100%;
    }

    @media (min-width: 1281px) {
        display: none;
    }
`;

export const DrawerContent = styled.div`
    height: 100%;
    width: 100%;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 14px 16px 12px;
    box-sizing: border-box;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colors.border.default};
        border-radius: 3px;
        
        &:hover {
            background: ${({ theme }) => theme.colors.text.secondary};
        }
    }
    
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.border.default} transparent;
`;

export const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
    position: relative;

    ${LogoImage} {
        height: 50px;
        width: 125px;
        object-fit: contain;
        margin: 0;
    }
`;

export const DrawerCloseButton = styled.button`
    background: transparent;
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    position: absolute;
    right: 0;
    
    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }};
        border-color: ${({ theme }) => theme.colors.text.primary};
    }
    
    .anticon {
        font-size: 18px;
    }
`;

export const OutsideContainer = styled.div`
    min-height: 100vh;
    width: 100%;
    display: flex;
    justify-content: center;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    padding: 0;
`;

export const OutsideLayoutContent = styled(Layout.Content)`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100vh;
    padding: 0;
    background: transparent;
`;

export const OutsideContent = styled.div`
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    width: 100%;
    gap: ${({ theme }) => theme.spacing.xl};
    min-height: calc(100vh - ${({ theme }) => theme.spacing.xl} * 2);
    justify-content: center;
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        position: relative;
        display: block;
        grid-template-columns: none;
        gap: 0;
        min-height: 100vh;
    }
`;

